import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { EventsTableHeaderStyle } from 'src/domains/sportsbook/webview/components/eventsTable/EventsMainSectionWrapper';
import { PromotedSportSingleType } from 'src/domains/layouts/webview/components/promotedSports/PromotedEventsState.state';
import { PromotedSportSectionWrapper } from 'src/domains/layouts/webview/components/promotedSports/PromotedSportSectionWrapper';
import { FiltersStyle } from 'src/domains/sportsbook/webview/components/eventsTable/EventsTable.style';
import { FiltersScrollableWrapper } from 'src/domains/layouts/webview/components/filters/FiltersScrollableWrapper';
import { Filter } from 'src/domains/layouts/webview/components/filters/Filters.style';
import { UniverseComponent } from 'src/domains/common/universes';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Collapse } from 'src/ui/components/collapse/sports/01/Collapse';
import { PromotedEventsWrapper, PromotedSportsWrapper } from './PromotedEventsWidget.style';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { SectionHeader } from 'src/ui/components/collapse/sports/05/SectionHeader';
import { FiltersSkin } from 'src/domains/layouts/webview/components/filters/FiltersSkin';
import { TranslationsStore } from 'src/domains/layouts/state/translationsStore/TranslationsStore';

interface PropsType {
    topMargin?: boolean;
}

export const PromotedEventsWidget = observer('PromotedEventsWidget', ({ topMargin = false }: PropsType) => {
    const common = useCommon();
    const { promotedEventsState } = AppLayoutsState.get(common);

    const promotedSports = promotedEventsState.promotedSportsToShow;
    const {
        componentVariants: { sportsbookVariant, filtersVariant },
        unifiedSportEventsTable,
    } = ConfigComponents.get(common).unifiedComponentConfig;

    const titleJsx = (
        <UniverseComponent>
            {(universe): React.ReactElement => (
                <span>{universe.promotedEventsSectionTitle(LanguagesState.get(common))}</span>
            )}
        </UniverseComponent>
    );

    if (promotedSports.length > 0) {
        return (
            <PromotedEventsWrapper
                variant={sportsbookVariant}
                topMargin={topMargin}
            >
                {unifiedSportEventsTable ? (
                    <SectionHeader
                        title={titleJsx}
                        dataTest='promoted-event-header'
                    />
                ) : (
                    <Collapse
                        title={titleJsx}
                        isOpen={promotedEventsState.openEvents}
                        handleToggle={promotedEventsState.setOpenEvents}
                        dataTest='promoted-event-header'
                    />
                )}
                {promotedEventsState.openEvents ? (
                    <>
                        <FiltersStyle
                            headerStyle={EventsTableHeaderStyle.SOLID}
                            isCompetitionPage={false}
                            variant={sportsbookVariant}
                        >
                            <FiltersScrollableWrapper>
                                {promotedEventsState.sportFiltersAll.map((sport) => {
                                    const translationsStore = TranslationsStore.get(common);
                                    const sportName =
                                        common.models.getSport(sport.key)?.name ??
                                        translationsStore.translateSport(sport.key);
                                    return (
                                        <Filter
                                            key={sport.key}
                                            title={sport.key === 'all' ? 'All Sports' : sportName}
                                            disabled={false}
                                            skin={unifiedSportEventsTable ? FiltersSkin.TERTIARY : FiltersSkin.PRIMARY}
                                            onClick={(): void =>
                                                promotedEventsState.handleToggleIsSportFilterActive(sport.key)
                                            }
                                            isActive={promotedEventsState.isFilterActive(sport.key)}
                                            data-test={`promoted-event-filter-${sportName.toLowerCase().replaceAll(' ', '')}`}
                                            variant={unifiedSportEventsTable ? sportsbookVariant : filtersVariant}
                                        >
                                            {sport.key === 'all' ? 'All Sports' : sportName}
                                        </Filter>
                                    );
                                })}
                            </FiltersScrollableWrapper>
                        </FiltersStyle>
                        <PromotedSportsWrapper variant={sportsbookVariant}>
                            {promotedSports.map((sportData: PromotedSportSingleType) => (
                                <React.Fragment key={sportData.id}>
                                    {promotedEventsState.sportFiltersAll.map((elem) => {
                                        if (elem.key === sportData.sport_id && sportData.competitions_list.length > 0) {
                                            return (
                                                <PromotedSportSectionWrapper
                                                    key={sportData.id}
                                                    sportSlug={sportData.sport_id}
                                                    sportData={sportData}
                                                />
                                            );
                                        }
                                    })}
                                </React.Fragment>
                            ))}{' '}
                        </PromotedSportsWrapper>
                    </>
                ) : null}
            </PromotedEventsWrapper>
        );
    }

    return null;
});
